import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Button.
 *
 * A control says exactly what it does (BRAND.md §6), so a button always has a visible label or, for
 * an icon-only button, a required `label` that becomes its accessible name. There is no way to
 * render one without a name.
 *
 * Its states (BRAND.md §4.10): rest, hover (a slight lift; the crimson fill goes darker), pressed
 * (scale 0.98), focus-visible (the kit's ring), disabled (muted but readable, with an optional
 * reason), busy and success. Busy keeps the label, so the button holds its width, swaps its icon for
 * an OrbitLoader, stays focusable and reports `aria-busy`. Success swaps the icon for a check for
 * ~900 ms, for an action that stays on the page.
 */
import { Slot } from '@radix-ui/react-slot';
import { forwardRef, useCallback, useEffect, useId, useRef, useState, } from 'react';
import { useUiText } from '@clavian/i18n/react';
import { cx } from '../lib/cx.js';
import { CheckIcon } from './icons.js';
import { OrbitLoader } from './motion.js';
/** How long the check stays before the button goes back to its icon. */
export const BUTTON_SUCCESS_MS = 900;
/** A flag that `flash()` turns on for `ms`: the success state of a button whose action stays on the page. */
export function useSuccessFlash(ms = BUTTON_SUCCESS_MS) {
    const [on, setOn] = useState(false);
    const timer = useRef(undefined);
    useEffect(() => () => clearTimeout(timer.current), []);
    const flash = useCallback(() => {
        clearTimeout(timer.current);
        setOn(true);
        timer.current = setTimeout(() => setOn(false), ms);
    }, [ms]);
    return [on, flash];
}
/**
 * The two props an async action's button wants, from a mutation's own state: `busy` while it is pending and
 * `success` for ~900 ms after it succeeds. Spread it: `<Button {...useButtonFeedback(save)}>`. For an action that
 * leaves the page (a dialog that closes), pass `{ isPending }` only and there is no success.
 */
export function useButtonFeedback(state) {
    const [flashing, flash] = useSuccessFlash();
    const was = useRef(false);
    const succeeded = state.isSuccess === true && !state.isPending;
    useEffect(() => {
        if (succeeded && !was.current)
            flash();
        was.current = succeeded;
    }, [succeeded, flash]);
    return { busy: state.isPending, success: flashing && !state.isPending };
}
export const Button = forwardRef(function Button({ variant = 'secondary', size = 'md', block = false, icon, label, busy = false, success = false, disabledReason, asChild = false, className, children, type, disabled, onClick, ...rest }, ref) {
    const Component = asChild ? Slot : 'button';
    const t = useUiText();
    const reasonId = useId();
    const showReason = !asChild && disabled === true && disabledReason !== undefined;
    const showSuccess = !asChild && success && !busy;
    return (_jsx(Component, { ...rest, ref: ref, type: asChild ? undefined : (type ?? 'button'), className: cx('clav-button', `clav-button--${variant}`, size !== 'md' && `clav-button--${size}`, block && 'clav-button--block', showSuccess && 'clav-button--success', 
        // Icon-only: a named button with nothing visible but its glyph is drawn square.
        !asChild && label !== undefined && (children === undefined || children === null || children === false) && 'clav-button--icon', className), disabled: asChild ? undefined : disabled, "aria-label": label ?? rest['aria-label'], "aria-busy": busy || undefined, "aria-disabled": busy || undefined, "aria-describedby": showReason ? reasonId : rest['aria-describedby'], title: showReason ? disabledReason : rest.title, onClick: (event) => {
            if (busy) {
                event.preventDefault();
                return;
            }
            onClick?.(event);
        }, children: asChild ? (children) : (_jsxs(_Fragment, { children: [busy ? (_jsx("span", { className: "clav-button__icon clav-button__orbit", "aria-hidden": "true", children: _jsx(OrbitLoader, { size: 16, label: t('busy') }) })) : showSuccess ? (_jsx("span", { className: "clav-button__icon clav-button__check", "aria-hidden": "true", children: _jsx(CheckIcon, {}) })) : icon ? (_jsx("span", { className: "clav-button__icon", children: icon })) : null, children, showReason ? (_jsx("span", { id: reasonId, className: "clav-visually-hidden", children: disabledReason })) : null] })) }));
});
//# sourceMappingURL=button.js.map